import React, { useState } from 'react';
import { Plug, CheckCircle, XCircle, RefreshCw, ExternalLink, Copy, Settings } from 'lucide-react';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: string;
  status: 'active' | 'inactive' | 'planned';
  category: 'payment' | 'analytics' | 'email' | 'push';
  webhookUrl?: string;
  apiKeyConfigured: boolean;
  docsUrl: string;
}

const INTEGRATIONS: Integration[] = [
  { id: 'copecart', name: 'CopeCart', description: 'Zahlungsabwicklung und Abo-Management. Webhooks für automatische Premium-Aktivierung.', icon: '💳', status: 'active', category: 'payment', webhookUrl: '/api/webhooks/copecart', apiKeyConfigured: true, docsUrl: 'https://docs.copecart.com' },
  { id: 'supabase', name: 'Supabase', description: 'Backend-as-a-Service: Auth, Datenbank, Storage, Edge Functions.', icon: '⚡', status: 'active', category: 'analytics', apiKeyConfigured: true, docsUrl: 'https://supabase.com/docs' },
  { id: 'gemini', name: 'Google Gemini', description: 'KI-Modell für den Fahryo Coach, Erklärungen und Bildgenerierung.', icon: '🤖', status: 'active', category: 'analytics', apiKeyConfigured: true, docsUrl: 'https://ai.google.dev/docs' },
  { id: 'sendgrid', name: 'SendGrid', description: 'Transaktionale E-Mails: Willkommen, Passwort-Reset, Reports.', icon: '📧', status: 'planned', category: 'email', apiKeyConfigured: false, docsUrl: 'https://docs.sendgrid.com' },
  { id: 'mixpanel', name: 'Mixpanel', description: 'Product Analytics: Events, Funnels, Retention-Analysen.', icon: '📊', status: 'planned', category: 'analytics', apiKeyConfigured: false, docsUrl: 'https://docs.mixpanel.com' },
  { id: 'onesignal', name: 'OneSignal', description: 'Push-Notifications für Streak-Erinnerungen und Updates.', icon: '🔔', status: 'planned', category: 'push', apiKeyConfigured: false, docsUrl: 'https://documentation.onesignal.com' },
  { id: 'sentry', name: 'Sentry', description: 'Error-Tracking und Performance-Monitoring.', icon: '🐛', status: 'planned', category: 'analytics', apiKeyConfigured: false, docsUrl: 'https://docs.sentry.io' },
];

const STATUS_CONFIG = {
  active: { label: 'Aktiv', badge: 'badge-success', icon: <CheckCircle size={12} /> },
  inactive: { label: 'Inaktiv', badge: 'badge-danger', icon: <XCircle size={12} /> },
  planned: { label: 'Geplant', badge: 'badge-neutral', icon: <Settings size={12} /> },
};

const CATEGORY_LABELS = { payment: 'Zahlung', analytics: 'Analytics', email: 'E-Mail', push: 'Push' };

export const IntegrationsPage: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyWebhook = (url: string, id: string) => {
    navigator.clipboard.writeText(window.location.origin + url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const activeCount = INTEGRATIONS.filter(i => i.status === 'active').length;
  const plannedCount = INTEGRATIONS.filter(i => i.status === 'planned').length;

  return (
    <div>
      <div className="page-header"><h1><Plug size={24} /> API-Integrationen</h1></div>

      <div className="grid-cols-3 mb-6">
        <div className="kpi-card"><span className="kpi-label">Aktive Integrationen</span><span className="kpi-value" style={{ color: 'var(--success)' }}>{activeCount}</span></div>
        <div className="kpi-card"><span className="kpi-label">Geplant</span><span className="kpi-value">{plannedCount}</span></div>
        <div className="kpi-card"><span className="kpi-label">Gesamt</span><span className="kpi-value">{INTEGRATIONS.length}</span></div>
      </div>

      {/* Active Integrations */}
      <h3 style={{ marginBottom: 12 }}>🟢 Aktive Integrationen</h3>
      <div className="grid-cols-3 mb-6" style={{ gap: 12 }}>
        {INTEGRATIONS.filter(i => i.status === 'active').map(i => {
          const sc = STATUS_CONFIG[i.status];
          return (
            <div key={i.id} className="card" style={{ borderLeft: '3px solid var(--success)' }}>
              <div className="flex items-center gap-2 mb-2">
                <span style={{ fontSize: 24 }}>{i.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{i.name}</div>
                  <span className={`badge ${sc.badge}`} style={{ fontSize: 10 }}>{sc.icon} {sc.label}</span>
                </div>
                <a href={i.docsUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                  <ExternalLink size={12} />
                </a>
              </div>
              <p className="text-xs text-muted" style={{ marginBottom: 8 }}>{i.description}</p>
              <div className="flex items-center gap-2 text-xs">
                <span className="badge badge-success" style={{ fontSize: 10 }}>API Key ✓</span>
                {i.webhookUrl && (
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => copyWebhook(i.webhookUrl!, i.id)}>
                    <Copy size={10} /> {copied === i.id ? 'Kopiert!' : 'Webhook URL'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Planned Integrations */}
      <h3 style={{ marginBottom: 12 }}>📋 Geplante Integrationen</h3>
      <div className="grid-cols-2 mb-6" style={{ gap: 12 }}>
        {INTEGRATIONS.filter(i => i.status === 'planned').map(i => {
          const sc = STATUS_CONFIG[i.status];
          return (
            <div key={i.id} className="card" style={{ opacity: 0.7, borderStyle: 'dashed' }}>
              <div className="flex items-center gap-2 mb-2">
                <span style={{ fontSize: 20 }}>{i.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{i.name}</div>
                  <span className={`badge ${sc.badge}`} style={{ fontSize: 10 }}>{sc.label}</span>
                </div>
                <span className="badge badge-neutral" style={{ fontSize: 10 }}>{CATEGORY_LABELS[i.category]}</span>
              </div>
              <p className="text-xs text-muted">{i.description}</p>
            </div>
          );
        })}
      </div>

      {/* Webhook Info */}
      <div className="card" style={{ background: 'var(--bg-elevated)' }}>
        <h3 style={{ marginBottom: 8 }}>Webhook-Endpunkte</h3>
        <table style={{ fontSize: 13 }}>
          <thead><tr><th>Service</th><th>Endpunkt</th><th>Status</th></tr></thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 600 }}>CopeCart</td>
              <td className="text-mono text-xs">/api/webhooks/copecart</td>
              <td><span className="badge badge-success">Aktiv</span></td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Supabase Auth</td>
              <td className="text-mono text-xs">/api/webhooks/auth</td>
              <td><span className="badge badge-success">Aktiv</span></td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>SendGrid Inbound</td>
              <td className="text-mono text-xs">/api/webhooks/email</td>
              <td><span className="badge badge-neutral">Geplant</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
